import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserService } from './user.service';
import { ConsoleHelper } from './static-classes/console-helper';

// Universal interface for SignalR messages
export interface SignalRMessage {
    userId?: string; // User ID for filtering (optional if missing from backend)
    name?: string; // Message name: "CalculationStateChanged", "ProjectUpdated"
    data: string; // Payload data as JSON string
    timestamp?: Date; // Timestamp when sent
}

@Injectable({
    providedIn: 'root',
})
export class SignalRService {
    private hubConnection: HubConnection | null = null;
    private readonly connectionState = new BehaviorSubject<string>('Disconnected');
    private readonly messages = new BehaviorSubject<SignalRMessage[]>([]);
    private currentUser: any = null;

    constructor(private readonly userService: UserService) {
        // Subscribe to user changes
        this.userService.userStream.subscribe(async user => {
            this.currentUser = user;
            // If SignalR is connected and we have a user, join the group
            if (this.isConnected() && user?.id) {
                await this.joinUserGroup();
            }
        });
    }

    /**
     * Connect to SignalR Hub
     */
    public async startConnection(): Promise<void> {
        try {
            const urlAndConfig = this.getSignalRUrlFromEnvironment();
            const url = urlAndConfig.url;
            const enableLogging = urlAndConfig.enableLogging;

            ConsoleHelper.log(`Starting SignalR connection to: ${url}`);

            // Create connection
            this.hubConnection = new HubConnectionBuilder()
                .withUrl(url)
                .withAutomaticReconnect()
                .configureLogging(enableLogging ? LogLevel.Information : LogLevel.Error)
                .build();

            // Connection event handlers
            this.hubConnection.onclose(error => {
                ConsoleHelper.log('SignalR connection closed:', error);
                this.connectionState.next('Disconnected');
            });

            this.hubConnection.onreconnecting(error => {
                ConsoleHelper.log('SignalR reconnecting:', error);
                this.connectionState.next('Reconnecting');
            });

            this.hubConnection.onreconnected(async connectionId => {
                ConsoleHelper.log('SignalR reconnected with ID:', connectionId);
                this.connectionState.next('Connected');
                // Rejoin user group after reconnection
                await this.joinUserGroup();
            });

            // Register message handler
            this.hubConnection.on('ReceiveNotification', (message: SignalRMessage) => {
                ConsoleHelper.log('SignalR notification received:', message);
                this.handleNotification(message);
            });

            // Connect
            await this.hubConnection.start();
            ConsoleHelper.log('SignalR connected successfully');
            this.connectionState.next('Connected');

            // Join user group after successful connection
            await this.joinUserGroup();
        } catch (error) {
            console.error('Error starting SignalR connection:', error);
            this.connectionState.next('Error');
            throw error;
        }
    }

    /**
     * Disconnect from SignalR Hub
     */
    public async stopConnection(): Promise<void> {
        if (this.hubConnection) {
            try {
                // Leave user group before disconnecting
                await this.leaveUserGroup();
                await this.hubConnection.stop();
                ConsoleHelper.log('SignalR connection stopped');
                this.connectionState.next('Disconnected');
            } catch (error) {
                console.error('Error stopping SignalR connection:', error);
            }
        }
    }

    /**
     * Join user group for targeted notifications
     */
    private async joinUserGroup(): Promise<void> {
        if (!this.hubConnection) {
            console.warn('Cannot join user group: no SignalR connection');
            return;
        }

        // Wait for user to be loaded if not available yet
        if (!this.currentUser?.id) {
            ConsoleHelper.log('User ID not loaded yet, will join group when user is available');
            return; // User will be loaded later and we'll join then
        }

        try {
            const groupName = this.currentUser.id; // Use user ID as group name
            await this.hubConnection.invoke('JoinUserGroup', groupName);
            ConsoleHelper.log(`Joined SignalR group: ${groupName}`);
        } catch (error) {
            console.error('Error joining user group:', error);
        }
    }

    /**
     * Leave user group
     */
    private async leaveUserGroup(): Promise<void> {
        if (!this.hubConnection || !this.currentUser?.id) {
            return;
        }

        try {
            const groupName = this.currentUser.id;
            await this.hubConnection.invoke('LeaveUserGroup', groupName);
            ConsoleHelper.log(`Left SignalR group: ${groupName}`);
        } catch (error) {
            console.error('Error leaving user group:', error);
        }
    }

    /**
     * Connection status
     */
    public getConnectionState(): Observable<string> {
        return this.connectionState.asObservable();
    }

    /**
     * All messages for current user
     */
    public getMessages(): Observable<SignalRMessage[]> {
        return this.messages.asObservable();
    }

    /**
     * Check connection status
     */
    public isConnected(): boolean {
        return this.hubConnection?.state === 'Connected';
    }

    /**
     * Handle incoming notification
     */
    private handleNotification(message: SignalRMessage): void {
        // Add timestamp if missing
        if (!message.timestamp) {
            message.timestamp = new Date();
        }

        // 🔥 MESSAGE ANALYSIS LOGIC - ADD YOUR CUSTOM HANDLING HERE
        this.analyzeAndProcessMessage(message);

        // Add to message list
        const currentMessages = this.messages.value;
        const updatedMessages = [...currentMessages, message];

        // Keep only last 100 messages
        if (updatedMessages.length > 100) {
            updatedMessages.splice(0, updatedMessages.length - 100);
        }

        this.messages.next(updatedMessages);

        // Notification popup handled by GlobalNotificationsComponent
        // which subscribes to getMessages()
        ConsoleHelper.log('Message added to stream for global notifications');
    }

    /**
     * Analyze message name and execute specific logic
     */
    private analyzeAndProcessMessage(message: SignalRMessage): void {
        const messageName = message.name?.toLowerCase();

        ConsoleHelper.log(`🔍 Processing message: ${message.name}`);

        switch (messageName) {
            case 'calculationstatechanged':
                this.handleCalculationStateChanged(message);
                break;

            case 'projectupdated':
                this.handleProjectUpdated(message);
                break;

            case 'usergroupchanged':
                this.handleUserGroupChanged(message);
                break;

            case 'systemnotification':
                this.handleSystemNotification(message);
                break;

            default:
                ConsoleHelper.log(`📢 Unknown message type: ${message.name}`);
                this.handleGenericMessage(message);
                break;
        }
    }

    /**
     * Handle specific message types
     */
    private handleCalculationStateChanged(message: SignalRMessage): void {
        ConsoleHelper.log('⚙️ Calculation state changed:', message.data);
        // TODO: Add specific logic for calculation updates
        // Example: Parse data, update UI state, show progress
    }

    private handleProjectUpdated(message: SignalRMessage): void {
        ConsoleHelper.log('📁 Project updated:', message.data);
        // TODO: Add logic for project updates
        // Example: Refresh project data, update navigation
    }

    private handleUserGroupChanged(message: SignalRMessage): void {
        ConsoleHelper.log('👥 User group changed:', message.data);
        // TODO: Add logic for user group changes
        // Example: Update permissions, refresh user list
    }

    private handleSystemNotification(message: SignalRMessage): void {
        ConsoleHelper.log('🔔 System notification:', message.data);
        // TODO: Add logic for system-wide notifications
        // Example: Show maintenance notice, update status
    }

    private handleGenericMessage(message: SignalRMessage): void {
        ConsoleHelper.log('📨 Generic message:', message.data);
        // TODO: Default handling for unknown message types
    }

    /**
     * Build SignalR URL from API URL
     */
    private getSignalRUrl(apiUri: string): string {
        // Replace '/api' with '/signalR-hub' in the URL
        return apiUri.replace(/\/api\/?$/, '/signalR-hub');
    }

    /**
     * Get URL from environment configuration
     */
    private getSignalRUrlFromEnvironment(): { url: string; enableLogging: boolean } {
        // Use the same approach as other services - environment.apiUri is populated by AuthConfigService
        ConsoleHelper.log('🔍 Environment debug:', {
            apiUri: environment.apiUri,
            production: environment.production,
            current: environment.current,
        });

        if (!environment.apiUri) {
            throw new Error('❌ environment.apiUri is not loaded yet - SignalR cannot connect before environment is initialized. Please ensure AuthConfigService.loadEnvironment() has completed.');
        }

        const signalRUrl = this.getSignalRUrl(environment.apiUri);
        ConsoleHelper.log(`📡 Using SignalR URL: ${signalRUrl} (from environment.apiUri: ${environment.apiUri})`);

        return {
            url: signalRUrl,
            enableLogging: !environment.production,
        };
    }

    /**
     * Clear messages
     */
    public clearMessages(): void {
        this.messages.next([]);
    }
}
